import { GridApi, GridReadyEvent, ICellRendererParams, ViewportChangedEvent } from '@ag-grid-community/core';
import { Component, HostBinding, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { fromEvent, Subscription } from 'rxjs';
import { AgGridModule } from '@ag-grid-community/angular';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { NgIf, NgFor } from '@angular/common';

import { pageTransitions } from '../../../../shared/animations/page-transitions';
import {
	LoadingSpinnerCellComponent
} from '../../../../shared/datacenter/cell-renderers/loading-spinner-cell.component';
import { CommonColDef, IconColDef } from '../../../../shared/datacenter/column-defs';
import { clientSideAgGridModules, CommonGridOptions } from '../../../../shared/datacenter/grid-options';
import { User } from '../../../../shared/models/user';
import { TranslationService } from '../../../../shared/services/translation.service';
import { UserService } from '../../../../shared/services/user.service';
import { Service, UserServiceResponse } from '../../../models/user-service';
import { ParameterIdResponse } from '../../../models/v2/parameter-id-response';
import { UserCardService } from '../../../services/user-card.service';
import { CheckboxHeaderComponent } from '../../../../shared/datacenter/header-components/checkbox-header.component';

import { DialogDeleteServiceComponent } from './dialog-delete-service/dialog-delete-service.component';
import { DialogShowParametersComponent } from './dialog-show-user-parameters/dialog-show-parameters.component';

const resizeObservable = fromEvent(globalThis, 'resize');
const firstTabIndex = 0;

@Component({
	selector: 'copo-user-self-card-service',
	templateUrl: './user-self-card-service.component.html',
	styleUrl: './user-self-card-service.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,

		MatButtonModule,
		RouterLink,
		TranslateModule,
		MatIconModule,

		MatFormFieldModule,

		MatSelectModule,
		FormsModule,
		ReactiveFormsModule,
		NgFor,

		MatOptionModule,
		AgGridModule
	]
})
export class UserCardServiceComponent implements OnInit, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	userServiceInfoSelected?: UserServiceResponse;
	userServicesResponse: Service[] = [];
	userServicesOrganizations?: UserServiceResponse[];
	userId?: string;
	currentOrgId?: number;
	validUserCondition?: boolean;
	logedInUser?: User;
	organizationControl = new FormControl();
	parametersRequest?: ParameterIdResponse;
	readonly modules = clientSideAgGridModules;
	gridOptions: CommonGridOptions<Service>;

	supplierIndex = firstTabIndex;
	enableManageServices? = false;
	dialogSubscription: Subscription = new Subscription();
	gridApi?: GridApi<Service>;
	deleteServiceSub?: Subscription;
	private resizeSub?: Subscription;
	private routeSub?: Subscription;
	private userServicesSub: Subscription = Subscription.EMPTY;

	constructor(
		private readonly userCardService: UserCardService,
		private readonly userService: UserService,
		private readonly translationService: TranslationService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,

		readonly dialog: MatDialog
	) {
		this.gridOptions = new CommonGridOptions<Service>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			suppressRowClickSelection: true,
			rowSelection: 'multiple',
			domLayout: 'autoHeight',
			headerHeight: 50,
			loadingOverlayComponent: LoadingSpinnerCellComponent,
			columnDefs: [
				new CommonColDef<Service, string>({
					headerName: this.translationService.getTR('user.card.services.tab.name'),
					headerComponent: CheckboxHeaderComponent,
					field: 'name',
					sort: 'asc',
					sortable: true,
					comparator: (valueA, valueB) =>
						!valueA ? -1 : !valueB ? 1 : valueA.toString().localeCompare(valueB.toString()),
				}),
				new CommonColDef<Service>({
					headerName: this.translationService.getTR('user.card.services.tab.desc'),
					field: 'description',
				}),
				new IconColDef<Service>({
					minWidth: 80,
					maxWidth: 100,
					width: 100,
					cellStyle: { textAlign: 'center' },
					cellRendererParams: {
						icon: (params: ICellRendererParams<Service>) => {
							const serviceResponse = params.data;
							if (!serviceResponse) {
								return '';
							}
							if (serviceResponse.userDiffusionServiceParameter && this.enableManageServices
								&& serviceResponse.editable === 1) {
								return {

									iconName: 'settings',
									className: 'mat-accent',

								};
							}
							return '';
						},
						onClick: (params: ICellRendererParams<Service>) => () => {
							this.showUserParameterDialog(params.api, params.node.id);
						}
					},
				}),
				new IconColDef<Service>({
					headerName: this.translationService.getTR('organization.user-manage'),
					minWidth: 64,
					maxWidth: 64,
					width: 64,
					cellStyle: { textAlign: 'center' },
					cellRendererParams: {
						icon: () => {
							if (this.enableManageServices) {
								return {

									iconName: 'delete_bin',
									className: 'mat-accent',

								};
							}
							return '';
						},
						onClick: (params: ICellRendererParams<Service>) => () => {
							this.showDeletionDialog(params);
						}
					},
				})
			]
		});
	}

	ngOnInit() {
		if (this.route.parent === null) {
			throw new Error('wrong url');
		}
		this.routeSub = this.route.parent.paramMap.subscribe(params => {
			const idString = params.get('id');
			if (idString === null) {
				return;
			}
			this.userId = idString;
		});
		const loginUser = this.userService.loggedUser;
		if (loginUser === undefined) {
			return;
		}
		this.userId = this.router.url.includes('self') ? loginUser.id.toString() : this.userId;
		this.getUserServices(Number(this.userId), loginUser.currentVisibility.organizationId);
	}

	showDeletionDialog(event: ICellRendererParams<Service>) {
		if (event.node.id) {
			const service = event.api.getRowNode(event.node.id)?.data;
			if (!service) {
				return;
			}
			const dialogRef = this.dialog.open(DialogDeleteServiceComponent, {
				width: '60%',
				data: service.name
			});

			this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
				if (result && service.id) {
					this.deleteService(service.id);
				}
			});
		}
	}

	getRouteParam() {
		return this.userId;
	}

	ngOnDestroy() {
		if (this.resizeSub) {
			this.resizeSub.unsubscribe();
		}
		this.routeSub?.unsubscribe();
		this.userServicesSub.unsubscribe();
	}

	onGridReady(params: GridReadyEvent) {
		this.gridApi = params.api;
		params.api.sizeColumnsToFit();
		const resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
		if (!this.resizeSub) {
			this.resizeSub = resizeSub;
		} else {
			this.resizeSub.add(resizeSub);
		}
	}

	showUserParameterDialog(api: GridApi, nodeId?: string) {
		if (!nodeId) {
			return;
		}
		const userServicesResponse = <Service>api.getRowNode(nodeId)?.data;
		const dialogRef = this.dialog.open(DialogShowParametersComponent, {
			width: '60%',
			data: userServicesResponse.userDiffusionServiceParameter,
		});
		this.dialogSubscription = dialogRef.afterClosed().subscribe(async result => {
			if (!result) {
				return;
			}
			const showParameterResult = <Record<string, string> | boolean> result;
			const addOrUpdateParameterRequestList: ParameterIdResponse[] = [];
			if (!userServicesResponse.userDiffusionServiceParameter) {
				return;
			}
			for (const diffusionServiceParameter of userServicesResponse.userDiffusionServiceParameter) {
				for (const parameterResult of Object.entries(showParameterResult)) {
					if (String(diffusionServiceParameter.serviceParameter.id) === parameterResult[0]
						&& (diffusionServiceParameter.value || diffusionServiceParameter.value !== ''
							|| diffusionServiceParameter.value	!== parameterResult[1])) {
						diffusionServiceParameter.value = String(parameterResult[1]);
						addOrUpdateParameterRequestList.push({
							id: diffusionServiceParameter.serviceParameter.id,
							value: String(parameterResult[1]),
						});
					}
				}
			}
			await this.addOrUpdateServiceParameters(
				Number(this.userId),
				Number(this.userServiceInfoSelected?.id),
				userServicesResponse.id,
				addOrUpdateParameterRequestList
			);
		});
	}

	changeButton(index: number) {
		this.supplierIndex = index;
	}

	onViewportChanged(params: ViewportChangedEvent) {
		params.api.sizeColumnsToFit();
	}

	handleOrganizationServicesUpdated(value: UserServiceResponse) {
		const loginUser = this.userService.loggedUser;
		if (loginUser === undefined) {
			return;
		}
		this.enableManageServices = loginUser.canUpdateOrganizationUsers(value.id);
		this.userServiceInfoSelected = value;
		this.currentOrgId = this.userServiceInfoSelected.id;
		if (this.gridApi) {
			this.gridApi.setGridOption('rowData', value.services);
		}
	}

	async addOrUpdateServiceParameters(
		userId: number,
		orgId: number,
		serviceId: string,
		parametersRequest: ParameterIdResponse[]
	) {
		try {
			await this.userService.addOrUpdateServiceParametersOfUser(userId, orgId, serviceId, parametersRequest);
			this.getUserServices(userId, orgId);
		} catch (err) {
			console.error('Error while adding the parameter value', err);
		}
	}

	private deleteService(id: string) {
		if (!this.userId || !this.userServiceInfoSelected?.supplier) {
			return;
		}
		const loggedInUser = this.userService.loggedUser;
		if (loggedInUser === undefined) {
			return;
		}
		this.deleteServiceSub = this.userCardService.deleteServicesFromUser(
			this.userId,
			this.userServiceInfoSelected.supplier.id,
			this.userServiceInfoSelected.id.toString(),
			[id]
		)
			.subscribe({
				next: () => {
					if (this.userServiceInfoSelected) {
						const index = this.userServiceInfoSelected.services.findIndex(service => service.id === id);
						this.userServiceInfoSelected.services.splice(index, 1);
						if (this.gridApi) {
							this.gridApi.setGridOption('rowData', this.userServiceInfoSelected.services);
						}
					}
				},
				error: (error: unknown) => {
					console.error(error);
				}
			});
	}

	private getUserServices(userId: number, defaultOrganizationId: number) {
		this.userServicesSub = this.userCardService.getServicesForUserByOrganization(userId)
			.subscribe({
				next: userServicesByOrganization => {
					const loginUser = this.userService.loggedUser;
					if (loginUser === undefined) {
						return;
					}
					this.userServicesOrganizations = userServicesByOrganization;
					if (this.gridApi && userServicesByOrganization.length === 0) {
						this.enableManageServices = loginUser.canUpdateOrganizationUsers(defaultOrganizationId);
						this.gridApi.setGridOption('rowData', []);
						return;
					}
					for (const organization of userServicesByOrganization) {
						if (organization.id === defaultOrganizationId) {
							this.enableManageServices = loginUser.canUpdateOrganizationUsers(defaultOrganizationId);
							this.userServiceInfoSelected = organization;
							this.organizationControl.setValue(organization);
							if (this.gridApi) {
								this.gridApi.setGridOption('rowData', organization.services);
							}
							return;
						}
					}
					this.userServiceInfoSelected = userServicesByOrganization[0];
					this.enableManageServices = loginUser.canUpdateOrganizationUsers(userServicesByOrganization[0].id);
					this.organizationControl.setValue(userServicesByOrganization[0]);
					if (this.gridApi) {
						this.gridApi.setGridOption('rowData', userServicesByOrganization[0].services);
					}
				},
				error: (err: unknown) => {
					console.error('Error from http get user services', err);
				},
			});
	}
}
